import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getReferencePreviewForKey, referenceContentUrl, referenceItemContentUrl } from '../../data/reference';
import type { ReferencePreview } from '../../data/types';
import { TemplateContentPreview } from './TemplateContentPreview';

interface ReferenceSourcedPreviewProps {
  templateId: string;
  propertyType: string;
  displayName: string;
  entryKey: string;
  /** Only meaningful for an Image pinned to one `source: collection` item. */
  collectionItem?: string;
}

type PreviewResult = { scope: string; preview: ReferencePreview } | null;

/** Reference/Library/pinned-Collection sourced Image/Table/DocumentDictionary — the content
 * already lives in Reference Data, so this just fetches and renders it (no live call). */
export function ReferenceSourcedPreview({
  templateId,
  propertyType,
  displayName,
  entryKey,
  collectionItem
}: ReferenceSourcedPreviewProps) {
  const { t } = useTranslation();
  const key = `${templateId}:${entryKey}`;
  const [settled, setSettled] = useState<ReadonlyMap<string, PreviewResult>>(new Map());

  useEffect(() => {
    if (settled.has(key)) return;
    let cancelled = false;

    getReferencePreviewForKey(templateId, entryKey)
      .then((result) => {
        if (!cancelled) setSettled((current) => new Map(current).set(key, result));
      })
      .catch(() => {
        if (!cancelled) setSettled((current) => new Map(current).set(key, null));
      });

    return () => {
      cancelled = true;
    };
  }, [templateId, entryKey, key, settled]);

  const result = settled.get(key);

  if (result === undefined) {
    return <p className="mt-1 text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  if (result === null) {
    return <p className="mt-1 text-sm text-muted-foreground">{t('wizard.form.previewUnavailable')}</p>;
  }

  const { scope, preview } = result;
  const { entry } = preview;

  if (propertyType === 'Image') {
    // `collectionItem` only makes sense once the resolved entry is actually an imageCollection —
    // otherwise it's a stale leftover from when this property (or its Default value) pointed at a
    // different, collection-kind entry, and building an item URL against a plain file 404s.
    const src =
      collectionItem && entry.kind === 'imageCollection'
        ? referenceItemContentUrl(scope, entryKey, collectionItem)
        : referenceContentUrl(scope, entryKey);
    return <TemplateContentPreview image={{ src, alt: entry.title || displayName }} />;
  }

  if (propertyType === 'Table') {
    return <TemplateContentPreview table={{ columns: preview.columns, rows: preview.rows }} />;
  }

  return (
    <TemplateContentPreview
      document={{
        fileName: entry.fileName,
        description: entry.description,
        downloadHref: preview.hasContent ? referenceContentUrl(scope, entryKey, true) : undefined
      }}
    />
  );
}
